'use client';

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import Image from 'next/image';
import { useSiteTheme } from '@/components/SiteThemeProvider';
import type { ChessProgress, LabModel, SceneState, SceneStatus, createLabScene } from './labSceneRuntime';
import { sceneAsset, sceneScale } from './sceneAssets';
import { createSceneRecovery } from './sceneRecovery';
import type { ElectronicsState } from './sceneElectronicsControl';
import type { AIState } from './sceneAIControl';
import type { HeroLetterState } from './sceneHeroMorph';
import { heroThemeLabels, type HeroTheme } from './sceneAssets';
import styles from './lab-scene.module.css';

type Props = Omit<SceneState, 'visible' | 'theme'> & { onHeroNavigate?: (theme: HeroTheme) => void };
const labels: Record<LabModel, string> = {
  hero: 'Объёмный логотип LISAD', robotics: 'Робот-шахматист', ai: 'Искусственный интеллект',
  electronics: 'Электроника и датчики', software: 'Программные системы',
};

export default function LabScene(props: Props) {
  const { theme } = useSiteTheme();
  const hostRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const runtimeRef = useRef<ReturnType<typeof createLabScene> | null>(null);
  const recoveryRef = useRef<ReturnType<typeof createSceneRecovery> | null>(null);
  const sceneProps = { ...props, theme };
  const latest = useRef(sceneProps);
  latest.current = sceneProps;
  const [enabled, setEnabled] = useState(false);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<SceneStatus>('loading');
  const [attempt, setAttempt] = useState(0);
  const [moveBusy, setMoveBusy] = useState(false);
  const [chessProgress, setChessProgress] = useState<ChessProgress>({ phase: 'ready', move: '', description: 'Человек играет белыми, робот — чёрными. Можно начать партию.' });
  const [electronicsState, setElectronicsState] = useState<ElectronicsState>('exploded');
  const [aiState, setAIState] = useState<AIState>('idle');
  const [heroState, setHeroState] = useState<HeroLetterState | null>(null);
  const latestVisible = useRef(visible);
  latestVisible.current = visible;

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      const shown = entries.some(entry => entry.isIntersecting);
      setVisible(shown);
      if (shown) setEnabled(true);
    }, { threshold: 0.01 });
    if (rootRef.current) observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const recovery = createSceneRecovery(() => setAttempt(value => value + 1));
    recoveryRef.current = recovery;
    const availability = () => recovery.setAvailable(latestVisible.current && !document.hidden && navigator.onLine);
    const wake = () => { availability(); recovery.wake(); };
    const host = hostRef.current;
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);
    window.addEventListener('offline', availability);
    window.addEventListener('focus', wake);
    host?.addEventListener('webglcontextrestored', wake, true);
    availability();
    return () => {
      recovery.dispose();
      recoveryRef.current = null;
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', wake);
      window.removeEventListener('offline', availability);
      window.removeEventListener('focus', wake);
      host?.removeEventListener('webglcontextrestored', wake, true);
    };
  }, []);

  useEffect(() => { recoveryRef.current?.setAvailable(visible && !document.hidden && navigator.onLine); }, [visible]);

  useEffect(() => {
    if (!enabled || !hostRef.current) return;
    let cancelled = false;
    const report = (next: SceneStatus) => {
      if (cancelled) return;
      setStatus(next);
      recoveryRef.current?.setStatus(next);
    };
    report('loading');
    setMoveBusy(false);
    setChessProgress({ phase: 'ready', move: '', description: 'Человек играет белыми, робот — чёрными. Можно начать партию.' });
    setElectronicsState('exploded');
    setAIState('idle');
    setHeroState(null);
    void import('./labSceneRuntime').then(({ createLabScene: create }) => {
      if (cancelled || !hostRef.current) return;
      runtimeRef.current = create(hostRef.current, { ...latest.current, visible: latestVisible.current },
        report, busy => { if (!cancelled) setMoveBusy(busy); },
        next => { if (!cancelled) setElectronicsState(next); }, next => { if (!cancelled) setAIState(next); },
        next => { if (!cancelled) setChessProgress(next); }, next => { if (!cancelled) setHeroState(next); });
    }).catch(() => report('error'));
    return () => {
      cancelled = true;
      runtimeRef.current?.dispose();
      runtimeRef.current = null;
    };
  }, [enabled, attempt]);

  useLayoutEffect(() => { runtimeRef.current?.update({ ...props, visible, theme }); }, [props.model, props.transitionFrom, props.paused, props.reducedMotion, props.transitionProgress, visible, theme]);

  const ready = enabled && (status === 'ready' || status === 'transitioning');
  const chessLabel = moveBusy ? chessProgress.phase === 'checkmate' ? 'Шах и мат' : chessProgress.move || 'Идёт партия…'
    : chessProgress.phase === 'checkmate' ? 'Новая партия' : 'Сыграть партию';
  const assemblyBusy = electronicsState === 'assembling' || electronicsState === 'disassembling';
  const assemblyLabel = electronicsState === 'assembling' ? 'Сборка…' : electronicsState === 'disassembling' ? 'Разборка…'
    : electronicsState === 'assembled' ? 'Разобрать' : 'Собрать';
  const aiBusy = aiState === 'connecting' || aiState === 'disconnecting';
  const aiLabel = aiState === 'connecting' ? 'Подключение…' : aiState === 'disconnecting' ? 'Отключение…'
    : aiState === 'active' ? 'Отключить' : 'Тест сознания';
  return (
    <div ref={rootRef} className={styles.scene} data-scroll-driven={props.transitionFrom !== undefined} data-scene-model={props.model} data-scene-theme={theme} data-scene-status={enabled ? status : 'poster'}>
      <div className={`${styles.poster} ${ready ? styles.posterHidden : ''}`} style={{ '--scene-scale': sceneScale[props.model] } as CSSProperties}>
        <Image src={sceneAsset(props.model, 'png')} alt={labels[props.model]} fill sizes="100vw"
          style={{ objectFit: 'contain' }} priority={props.model === 'hero'} />
      </div>
      <div ref={hostRef} className={`${styles.canvasHost} ${ready ? styles.canvasReady : ''}`} aria-hidden="true" />
      {props.model === 'hero' && status === 'ready' && heroState && <button type="button" className={styles.heroLetterButton}
        data-hero-letter={heroState.letter} data-hero-theme={heroState.theme}
        aria-label={`Буква ${heroState.letter}: ${heroThemeLabels[heroState.theme]}. Перейти к разделу`}
        onClick={() => { const theme = runtimeRef.current?.heroDestination(); if (theme) props.onHeroNavigate?.(theme); }}>
        <span>{heroThemeLabels[heroState.theme]} <span aria-hidden="true">↗</span></span>
      </button>}
      {props.model === 'robotics' && ready && <button type="button" className={styles.moveButton}
        disabled={moveBusy || props.paused || props.reducedMotion || status !== 'ready'}
        data-move-control={moveBusy ? 'busy' : props.paused || props.reducedMotion || status !== 'ready' ? 'disabled' : 'ready'}
        aria-label={chessLabel} aria-busy={moveBusy}
        onClick={() => runtimeRef.current?.playMove()}>
        <span className={styles.moveIndicator} aria-hidden="true" />
        <span>{chessLabel}</span>
      </button>}
      {props.model === 'robotics' && ready && <span className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">{chessProgress.description}</span>}
      {props.model === 'electronics' && ready && <>
        <button type="button" className={styles.moveButton}
          disabled={assemblyBusy || status !== 'ready'} data-assembly-control={electronicsState}
          aria-label={assemblyLabel} aria-busy={assemblyBusy}
          onClick={() => runtimeRef.current?.toggleAssembly()}>
          <span className={styles.moveIndicator} aria-hidden="true" />
          <span>{assemblyLabel}</span>
        </button>
        <span className={styles.srOnly} role="status">{electronicsState === 'assembled' ? 'Устройство собрано.'
          : electronicsState === 'exploded' ? 'Устройство разобрано. Детали показаны отдельно.' : assemblyLabel}</span>
      </>}
      {props.model === 'ai' && ready && <>
        <button type="button" className={styles.moveButton}
          disabled={aiBusy || status !== 'ready'} data-ai-control={aiState}
          aria-label={aiLabel} aria-busy={aiBusy}
          onClick={() => runtimeRef.current?.toggleAI()}>
          <span className={styles.moveIndicator} aria-hidden="true" />
          <span>{aiLabel}</span>
        </button>
        <span className={styles.srOnly} role="status">{aiState === 'active' ? 'ИИ подключён. Нейронная сеть активна.'
          : aiState === 'idle' ? 'Нейронный каркас парит. Можно подключить ИИ.' : aiLabel}</span>
      </>}
      {enabled && status === 'loading' && <span className={styles.srOnly} role="status">Подготовка интерактивной сцены</span>}
    </div>
  );
}
